import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import MediaFrame from "../components/MediaFrame";
import Reveal from "../components/Reveal";

export default function Editions() {
  const [releases, setReleases] = useState([]);
  const [artists, setArtists] = useState([]);

  useEffect(() => {
    api.get("/releases").then((r) => setReleases(r.data)).catch(() => {});
    api.get("/artists").then((r) => setArtists(r.data)).catch(() => {});
  }, []);

  const artistName = (id) => artists.find((a) => a.id === id)?.name || "Joy Messenger";
  const accentOf = (id) => artists.find((a) => a.id === id)?.accent || "#C5A059";

  return (
    <main data-testid="editions-page" className="pt-28 sm:pt-36">
      <div className="jm-container pb-24 sm:pb-32">
        <Reveal>
          <section className="border-b border-jm-brass/15 pb-12 sm:pb-14" data-testid="editions-musique-image">
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-jm-brass mb-8">
              Collaborations et musique à l'image
            </h2>
            <div className="max-w-3xl space-y-5 text-[15px] text-jm-smoke leading-relaxed">
              <p className="font-serif text-lg sm:text-xl text-jm-bone leading-snug">
                L'univers musical de Joy Messenger résonne avec votre film, votre scénario ou votre projet ?
              </p>
              <p>
                Depuis plusieurs années, nous développons des passerelles entre musique, image et narration.
                Composition originale, création de paysages musicaux, recherche d'une identité sonore : nous aimons
                partir d'un récit, d'une atmosphère ou d'un personnage pour construire une musique qui ne se contente
                pas d'accompagner l'image, mais participe pleinement à son langage.
              </p>
              <p>
                Notre parcours nous a conduits à travailler pour Arte, France 2, le court métrage et le théâtre,
                notamment aux côtés d'Eugène Durif, Jeanine Beaude, Ghislaine Dumont, Bernard Menez, ainsi qu'avec des
                réalisateurs comme Fabien Gorgeart, les films des Fées, etc.
              </p>
              <div className="pt-6 mt-2 border-t border-jm-brass/10">
                <h3 className="font-serif text-2xl text-jm-bone mb-4">Catalogue &amp; édition musicale</h3>
                <p>
                  À l'initiative de Joy Messenger, Nicolas Dri développe depuis de nombreuses années un travail de
                  composition, d'arrangement et de réalisation musicale. Au fil de nombreuses collaborations
                  éditoriales, il a notamment constitué un vaste catalogue pour Wagram Music, explorant différentes
                  écritures instrumentales, musiques du monde et univers sonores destinés à l'édition et à l'image.
                </p>
                <Link
                  to="/catalogue-wagram"
                  data-testid="editions-wagram-link"
                  className="mt-4 inline-flex items-center gap-2 text-[15px] text-jm-brass hover:text-jm-amber transition-colors"
                >
                  Découvrir les archives et le catalogue Nicolas Dri × Wagram Music <ArrowUpRight size={14} />
                </Link>
              </div>
              <p>
                Ces différentes expériences nourrissent aujourd'hui une approche où jazz, musiques contemporaines,
                textures cinématographiques, acoustique, électronique et influences du monde peuvent se rencontrer selon
                les besoins du projet.
              </p>
              <p className="text-jm-bone">Réalisateurs, scénaristes, producteurs : nous sommes à l'écoute de toute collaboration.</p>
              <p>
                À partir de votre univers, nous pouvons rechercher ensemble une couleur, une matière, une signature
                musicale singulière — et imaginer la musique qui donnera à votre projet sa propre respiration.
              </p>
              <p className="font-serif italic text-lg text-jm-brass">
                Votre histoire. Notre écoute. Une identité musicale à inventer ensemble.
              </p>
            </div>
          </section>
        </Reveal>

        <Reveal delay={0.05}>
          <p className="jm-overline mt-14 sm:mt-16 mb-5">Catalogue Joy Messenger</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-jm-bone leading-[1.08] max-w-3xl">
            Les productions de la maison
          </h1>
          <p className="mt-6 max-w-2xl text-jm-smoke leading-relaxed">
            Tout ce que Joy Messenger soutient, réuni au même endroit.
          </p>
          <p className="mt-3 max-w-2xl text-jm-smoke leading-relaxed">
            Et pour flâner dans notre vivier musical, entre découvertes et melting-pot,{" "}
            <Link to="/jukebox" data-testid="editions-jukebox-link" className="text-jm-brass underline decoration-jm-brass/40 underline-offset-4 hover:text-jm-amber transition-colors">
              jetez une oreille au Jukebox
            </Link>
            .
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="releases-grid">
          {releases.map((r, i) => (
            <Reveal key={r.id} delay={0.06 * (i % 3)}>
              <article className="group h-full" data-testid={`release-card-${r.id}`}>
                <div className="relative overflow-hidden border border-jm-brass/15 group-hover:border-jm-brass/40 transition-colors duration-500">
                  <div className="aspect-square transition-transform duration-[1100ms] ease-out group-hover:scale-[1.04]">
                    <MediaFrame
                      label={r.title}
                      sublabel={`${r.type} · ${r.year}`}
                      accent={accentOf(r.artist)}
                      image={r.image}
                      className="w-full h-full"
                    />
                  </div>
                  <span className="absolute top-4 left-4 bg-jm-ink/80 backdrop-blur border border-jm-brass/25 text-jm-brass font-mono text-[9px] uppercase tracking-[0.2em] px-3 py-1.5">
                    {r.type}
                  </span>
                </div>
                <div className="pt-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-jm-mute">{artistName(r.artist)} — {r.year}</p>
                  <h2 className="mt-2 font-serif text-2xl text-jm-bone group-hover:text-jm-brass transition-colors duration-300">
                    {r.title}
                  </h2>
                  <p className="mt-3 text-sm text-jm-smoke leading-relaxed">{r.description}</p>
                  {r.listen_url ? (
                    <a
                      href={r.listen_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`release-listen-${r.id}`}
                      className="mt-4 inline-flex items-center gap-2 text-[13px] text-jm-brass hover:text-jm-amber transition-colors"
                    >
                      Écouter <ArrowUpRight size={13} />
                    </a>
                  ) : (
                    <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-jm-mute">Lien d'écoute à venir</p>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </main>
  );
}
